import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from '../../../statspro/js/components/common/stats-section';
import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';

import {
    RoundSelectNavItem,
    CompNavItem,
    ResponsiveSeasonNavItem,
    ResponsiveRoundNavItem,
    LiveToggleNavItem
} from 'common/react/components/competition-nav/nav-items';

import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useWindowWidth } from 'common/react/hooks/use-window-resize';
import { useGetCurrentCompFilters } from 'common/react/components/competition-nav/hooks/use-get-current-comp-filters';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';
import { FilterButton } from 'common/react/components/FilterButton';
import { getTranslation } from 'common/react/utils/translations';
import { initialiseNav } from 'common/store/modules/competition-nav/thunks';
import { useGetUrlParams } from 'common/react/components/competition-nav/hooks/use-get-url-params';
import { useSyncUrlParams } from 'common/react/hooks/use-sync-url-params';
import { useResetToDefaults } from 'common/react/components/competition-nav/hooks/use-reset-to-defaults';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';

/**
 * @constant {Array} Competitions we want to appear in the filter for the ladder
 */
export const ALLOWED_COMPS = window.LADDER_DEFAULT_COMP_ORDER
    ? window.LADDER_DEFAULT_COMP_ORDER.split(',')
    : [
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.WOMENS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.PRE_SEASON,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.VFL_PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.VFL_WOMENS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.SANFL_PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.U18_BOYS_CHAMPIONSHIPS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.U18_GIRLS_CHAMPIONSHIPS
      ];

export const LadderNav = ({ competitionId, seasonId, title }) => {
    const windowWidth = useWindowWidth();
    const dispatch = useDispatch();

    const isResponsive = () => windowWidth <= PULSE.app.measurements.desktop;
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth]);

    const params = useGetUrlParams();

    const initialValues = {
        competitionId: params.competition || competitionId,
        seasonId: params.season || seasonId,
        roundId: params.round || ''
    };

    useEffect(() => {
        dispatch(
            initialiseNav({ initialValues: initialValues, removeFinals: true })
        );
    }, [dispatch]);

    useSyncUrlParams({
        selector: navSelector,
        mapToParams: (items) => ({
            Competition: items.competition.id,
            Season: items.season.id,
            Round: items.round.id
        })
    });

    const nav = useSelector(navSelector);

    const options = useCompetitionNavigationOptions(nav);

    const { competitions, seasons } = options;

    const currentFilters = useGetCurrentCompFilters();
    const competitionNavRef = useRef();

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: nav,
        options,
        filtersFields: ['competition', 'season', 'round'],
        filtersRef: competitionNavRef
    });

    const { hasChangedFromDefaults, isSeasonChanged, isRoundChanged } =
        useHasChangedFromDefaults({
            state: {
                nav: nav
            },
            initialValues: {
                competitionId,
                seasonId,
                roundId: nav?.round?.initialItemId
            }
        });

    const resetToDefaults = useResetToDefaults({
        initialValues: {
            competitionId,
            seasonId,
            roundId: nav?.round?.initialItemId || ''
        },
        removeFinals: true
    });

    const renderMobile = () => (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    allowListOrder={ALLOWED_COMPS}
                    title={title}
                    hideLabel
                    removeFinals
                />
                <>
                    <FilterButton
                        classes="fixtures-nav__filter-button"
                        onChange={() =>
                            setAreFilterOptionsVisible((prev) => !prev)
                        }
                        areFilterOptionsVisible={areFilterOptionsVisible}
                        resetFilterBtnStyles={resetFilterBtnStyles}
                        setResetFilterBtnStyles={setResetFilterBtnStyles}
                        showFilterLabel={false}
                        showActiveCircle
                        isFilterSelectedCustom={() =>
                            isSeasonChanged || isRoundChanged
                        }
                    />
                    <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                        <SettingsButton />
                    </StatsSection.SettingsButton>
                </>
            </StatsSection.NavItems>
            {areFilterOptionsVisible && (
                <StatsSection.NavFilterItems
                    initialValues={{
                        competitionId,
                        seasonId
                    }}
                    hideReset
                    onReset={resetToDefaults}
                >
                    <ResponsiveSeasonNavItem
                        seasons={seasons}
                        type="with-default"
                        title={title}
                        removeFinals
                    />
                    <ResponsiveRoundNavItem
                        removeFinals
                        selector={navSelector}
                        hideLabel
                    />
                    {hasChangedFromDefaults ? (
                        <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                            <button
                                className="stats-leaders-filter-reset-button"
                                onClick={resetToDefaults}
                            >
                                {getTranslation(
                                    'label.statsLeaders.filters.resetButtonText'
                                )}
                            </button>
                        </div>
                    ) : null}
                </StatsSection.NavFilterItems>
            )}
            {showResponsiveItems && currentFilters?.round?.providerId && (
                <StatsSection.ResponsiveRowItems>
                    <LiveToggleNavItem
                        roundId={currentFilters.round.providerId}
                        labelRightPosition
                    />
                </StatsSection.ResponsiveRowItems>
            )}
        </>
    );

    const renderDesktop = () => (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    allowListOrder={ALLOWED_COMPS}
                    title={title}
                    hideLabel
                    removeFinals
                />
                <FilterDropdown
                    title={title}
                    icon="season-trophy"
                    navItem="season"
                    items={seasons}
                    removeFinals
                />
                <RoundSelectNavItem
                    removeFinals={true}
                    classes={'u-margin-right-auto'}
                    title={title}
                />
                {hasChangedFromDefaults ? (
                    <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                        <button
                            className="stats-leaders-filter-reset-button"
                            onClick={resetToDefaults}
                        >
                            {getTranslation(
                                'label.statsLeaders.filters.resetButtonText'
                            )}
                        </button>
                    </div>
                ) : null}
            </StatsSection.NavItems>
            <StatsSection.NavFilterItems
                title={title}
                filterKeys={['competition', 'season', 'round']}
            ></StatsSection.NavFilterItems>
            <StatsSection.SettingsButton>
                {!showResponsiveItems && currentFilters?.round?.providerId && (
                    <LiveToggleNavItem
                        roundId={currentFilters.round.providerId}
                        classes={
                            'competition-nav__live-toggle-wrapper competition-nav__live-toggle-wrapper--ladder'
                        }
                    />
                )}
                <SettingsButton />
            </StatsSection.SettingsButton>
        </>
    );

    return (
        <div
            className="competition-nav filter-dropdown rounded-filter-wrapper"
            ref={competitionNavRef}
        >
            <StatsSection.Nav
                showResponsiveItems={showResponsiveItems}
                withScrollableFilter={!showResponsiveItems}
            >
                {showResponsiveItems ? renderMobile() : renderDesktop()}
            </StatsSection.Nav>
        </div>
    );
};

LadderNav.propTypes = {
    competitionId: PropTypes.string,
    competitionProviderId: PropTypes.string,
    seasonPid: PropTypes.string,
    seasonId: PropTypes.string,
    title: PropTypes.string
};
